import { ArticleGridCards } from "@components/Cards/Layout/ArticleGridCards";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import fetchTablaApi from "@helper/fetchTablaApi";
import { isArrayWithElements } from "@helper/utils";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@hooks/useLoadMore/CreateLoadMoreHandler";
import LoadMore from "@hooks/useLoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@hooks/useLoadMore/SetFirstRenderedArticleIds";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { AdvertisementTypeEnum, PageAdTargetValue } from "@typings/Ads.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import { ReactElement, useState } from "react";
import { twMerge } from "tailwind-merge";

type KeywordInfiniteScrollProps = {
  keywordArticles: ProcessedArticleData[];
  tagAlias: string;
  pageAdTargetValue: PageAdTargetValue;
};

const PAGE_SIZE = 8;
const INITIAL_ARTICLE_COUNT = 24;

export default function KeywordInfiniteScroll({
  keywordArticles,
  tagAlias,
  pageAdTargetValue,
}: KeywordInfiniteScrollProps): ReactElement {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const showLoadMore: boolean = keywordArticles.length == INITIAL_ARTICLE_COUNT;
  useSetFirstRenderedArticleIds(keywordArticles, setRenderedArticleIds);
  const TablaCustomGaEvent = useTablaCustomEventAnalyticsCall();

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? keywordArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: tagAlias,
      sort: lastLoadedArticle?.sort,
    };

    const fetchMoreTagsStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        tagAlias: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "8",
      }).toString();

      const response: ProcessedArticleData[] = await fetchTablaApi(
        `more-tags-stories?${queryParams}`,
        "GET",
      );
      return response;
    };
    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreTagsStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };

    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    if (isArrayWithElements(response)) {
      TablaCustomGaEvent({
        event: "virtual_pv",
        pagination: pageNumber == 0 ? 1 : pageNumber,
      });
    }
    return response;
  };

  const loadedSections = [...loadMoreData].reduce<ProcessedArticleData[][]>(
    (sections, context, index) => {
      if (index % PAGE_SIZE === 0) sections.push([]);
      sections[sections.length - 1].push(context);
      return sections;
    },
    [],
  );

  return (
    <>
      <div className="w-full">
        {loadedSections.map((section, sectionIndex) => (
          <>
            <CompanionAdsLb
              adType={AdvertisementTypeEnum.LB3}
              uniqueSectionName="keywords"
              index={sectionIndex + 3}
              pageAdTargetType={pageAdTargetValue}
              rootClassName={
                "my-10 relative w-full bg-grey-950 py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
              }
            />
            <ArticleGridCards
              article={section}
              articleIndex={sectionIndex}
              rootClassName={twMerge(
                "px-3 xl:p-0 gap-3 md:inline-grid md:grid-cols-4 xmd:inline-grid lg:grid xmd:pb-10 lg:max-w-7xl bg-white m-auto",
                loadedSections.length - 1 === sectionIndex && "mb-5",
              )}
              cardWrapper="gap-2 xmd:py-0 md:flex-col md:max-w-[210px] xmd:max-w-full"
              imageWrapperStyle="md:max-w-[171px] xmd:w-full"
            />
          </>
        ))}
        {showLoadMore && hasMoreStories ? (
          <div className="flex w-full justify-center mb-xl mt-10 max-w-7xl bg-white m-auto px-3 xl:px-0">
            <LoadMore
              rootClassName="my-4 w-full "
              onLoadMore={handleLoadMore}
              hasMore={hasMoreStories}
              loadText="Load More"
              mobileInfiniteScroll={true}
              loadDefaultGaEvent={false}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
